/*
A paged, sortable table for the manager's list tabs.

Grown out of the products screen, which was the only tab that had a real list
while everything else hid behind "provide at least four characters to search".
Nothing here is products-specific: a caller passes rows, a column description
and the paging/sorting state, and the server does the paging.

Columns: { field, fieldFunc, headerName, type, align, sortable, hide, isId,
content, description, conditionalClassName }. Mark one column isId where the
rows have an identity; see the note by the row keys for what happens when they
do not.

`field` may be a dot path ('user.email'), resolved the way mongo does it.
`fieldFunc(row)` takes precedence over `field` when present.

`type` drives the built-in formatter: 'string' | 'date' | 'boolean' | 'action'.
'date' expects epoch millis, which is how this codebase stores them, and
'action' renders `content(row, idValue)`.

A tab whose cells need anything else passes its own `formatValue` rather than
adding a case here - that is what kept this component reusable. It receives
({ column, value, row, idValue }) and its return value is rendered as-is. The
same function usually backs the tab's CSV export, which is why it lives in the
container rather than in here.

Optional props, all defaulting to the behaviour this table had before the five
per-tab copies (Users, Comments, Logs, ProReservations, Subscriptions) were
folded back into it: stickyHeader, formatValue, onRowClick, rowStyle.

Markup is a plain <table> in a plain <div>; it used to be MUI's Card, Table and
TablePagination. Styling moved to styles/_table.scss, which keeps MUI's metrics
so the tabs look the way they did.
*/
// This module is based on https://dev.to/cubejs/react-data-table-with-material-ui-and-a-spark-of-joy-50o1

import React from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { ChevronUp, ChevronDown } from 'lucide-react';
import TablePagination from './TablePagination';
// Row/value resolution lives in a plain .js module so `npm test` can cover it - the test
// runner is plain node and cannot load JSX. See src/utils/dataTableRows.test.js.
import {
  readCell,
  findIdKey,
  resolveRowKey,
  resolveCellKey,
  defaultFormatValue,
} from '../../utils/dataTableRows';

const TableComponent = (props) => {
  const {
    tableData,
    columns,
    total,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    sorting,
    setSorting,
    stickyHeader,
    formatValue,
    onRowClick,
    rowStyle,
  } = props;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Which way the arrow points for the column currently being sorted on.
  const sortArrow = direction => (
    direction === -1 ? <ChevronUp size={16} /> : <ChevronDown size={16} />
  );

  const handleSetSorting = (str) => {
    setSorting([str, sorting[1] === -1 ? 1 : -1]);
  };

  if (!tableData) return null;

  // Every row needs a stable React key. Marking a column isId is the good case: the key
  // is then the row's own identity and survives re-sorts and refetches.
  //
  // Not every tab can manage that - the logs query returns level/message/messageMore/
  // createdAt and no id at all - so a missing isId falls back to the row's position
  // rather than throwing and taking the whole screen down. Position is a weaker key, but
  // paging and sorting both refetch and rebuild the body anyway, so within one page it
  // holds. It is still far better than the Math.random() the per-tab copies used, which
  // guaranteed a fresh key every render and threw away every row each time.
  //
  // The one thing a fallback row cannot do is address itself: idValue is undefined, so a
  // column of type 'action' needs a real isId. Every tab that has action columns has one.
  const idKey = findIdKey(columns);

  const visibleColumns = columns.filter(column => !column.hide);
  const format = formatValue || defaultFormatValue;

  const tableClass = [
    'ps-table',
    stickyHeader ? 'ps-table--sticky' : '',
    onRowClick ? 'ps-table--clickable' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="ps-card">
      <PerfectScrollbar>
        <TablePagination
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100, 200, 500, 1000]}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
        <div className={`ps-table-scroll${stickyHeader ? ' ps-table-scroll--sticky' : ''}`}>
          <table className={tableClass}>
            <thead>
              <tr>
                {visibleColumns.map((column, index) => (
                  <th
                    align={column.align}
                    key={resolveCellKey(column, index)}
                    className={column.sortable ? 'is-sortable' : undefined}
                    title={column.description}
                    onClick={() => {
                      if (column.sortable) handleSetSorting(`${column.field}`);
                    }}
                  >
                    <span>{column.headerName}</span>
                    <span className="ps-table__arrow">
                      {sorting[0] === column.field && sortArrow(sorting[1])}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => {
                const idValue = idKey ? row[idKey] : undefined;
                return (
                  <tr
                    key={resolveRowKey(row, idKey, rowIndex)}
                    {...(onRowClick && { onClick: () => onRowClick(idValue, row) })}
                  >
                    {visibleColumns.map((column, index) => (
                      <td
                        align={column.align}
                        key={resolveCellKey(column, index)}
                        className={column.conditionalClassName && column.conditionalClassName(row)}
                        style={rowStyle ? rowStyle(row) : undefined}
                      >
                        {format({
                          column, value: readCell(column, row), row, idValue,
                        })}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PerfectScrollbar>
    </div>
  );
};

TableComponent.propTypes = {
  tableData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  // [field, 1 | -1]
  sorting: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  setSorting: PropTypes.func.isRequired,
  // Pins the header while the body scrolls, for the long list tabs.
  stickyHeader: PropTypes.bool,
  // ({ column, value, row, idValue }) => node
  formatValue: PropTypes.func,
  // (idValue, row) => void
  onRowClick: PropTypes.func,
  // (row) => style object, applied to every cell in that row
  rowStyle: PropTypes.func,
};

TableComponent.defaultProps = {
  stickyHeader: false,
  formatValue: null,
  onRowClick: null,
  rowStyle: null,
};

export default TableComponent;
